import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  TextField, 
  MenuItem,
  CircularProgress,
  IconButton,
  Checkbox,
  ListItemText,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import axios from 'axios';
import { useAuth } from '../../../../context/AuthContext';

// Lista de sectores de respaldo (se usa si la API no responde)
const FALLBACK_SECTORS = [
  { id: '1',  nombre: 'Tecnología y Software' },
  { id: '2',  nombre: 'Marketing y Publicidad' },
  { id: '3',  nombre: 'Diseño y Creatividad' },
  { id: '4',  nombre: 'Educación y Formación' },
  { id: '5',  nombre: 'Salud y Bienestar' },
  { id: '6',  nombre: 'Finanzas y Negocios' },
  { id: '7',  nombre: 'Construcción y Arquitectura' },
  { id: '8',  nombre: 'Turismo y Hostelería' },
  { id: '9',  nombre: 'Legal y Consultoría' },
  { id: '10', nombre: 'E-commerce y Retail' },
  { id: '11', nombre: 'Ingeniería' },
  { id: '12', nombre: 'Recursos Humanos' },
];

const BolsaOnboarding = ({ onFinished, initialRole = null }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(initialRole ? 2 : 1);
  const [loading, setLoading] = useState(false);
  const [sectors, setSectors] = useState(FALLBACK_SECTORS);
  const [professionalTypes, setProfessionalTypes] = useState([]);
  const [formData, setFormData] = useState({
    user_type: initialRole || '',
    main_industry: '',   // nombre del sector (columna real en bolsa_perfiles)
    company_name: '',    // nombre de la empresa
    experience_years: '',
    specialization: '',  // Cambiado a string para selección única
    bio: '',             // NUEVO: Biografía profesional
    skills: '',          // NUEVO: Habilidades extra
    availability: ''
  });

  const userId = user?.user_id || user?.id;

  // Cargar sectores y tipos de profesionales
  React.useEffect(() => {
    const fetchData = async () => {
      try {
        
        
        // Cargar sectores desde el HOLDING
        const resSectors = await axios.post('https://apiweb.hitpoly.com/ajax/bolsaController.php', {
          accion: 'getSectors'
        });
        if (resSectors.data.success && resSectors.data.data?.length > 0) {
          setSectors(resSectors.data.data);
        } else {
        }

        // Cargar tipos de profesionales desde el HOLDING
        const resProfiles = await axios.post('https://apiweb.hitpoly.com/ajax/bolsaController.php', {
          accion: 'getTiposProfesionales'
        });
        if (resProfiles.data.success && resProfiles.data.data?.length > 0) {
          setProfessionalTypes(resProfiles.data.data);
        }

        // --- NUEVO: Cargar datos sugeridos de Hitpoly ---
        if (userId) {
          const resSuggested = await axios.post('https://apiweb.hitpoly.com/ajax/bolsaController.php', {
            accion: 'getSuggestedProfileData',
            user_id: userId
          });
          if (resSuggested.data.success && resSuggested.data.data) {
            const sug = resSuggested.data.data;
            setFormData(prev => ({
              ...prev,
              bio: sug.bio || prev.bio,
              experience_years: sug.experience_years || prev.experience_years,
              skills: Array.isArray(sug.skills) ? sug.skills.join(', ') : prev.skills
            }));
          }
        }
      } catch (error) {
        }
    };
    fetchData();
  }, [userId]);

  const handleRoleSelection = (type) => {
    setFormData({ 
      ...formData, 
      user_type: type,
      // Inicializamos según el tipo: array para empresa (múltiple), string para profesional (único)
      specialization: type === 'enterprise' ? [] : '' 
    });
    setStep(2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Para selecciones múltiples, MUI pasa el array directamente en value
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      
      // Guardar perfil en la API del HOLDING (fuente de verdad central)
      const response = await axios.post('https://apiweb.hitpoly.com/ajax/bolsaController.php', {
        accion: 'saveStep',
        user_id: userId,
        step_data: {
          ...formData,
          onboarding_finished: 1,
          current_step: 3
        }
      });
      
      

      if (response.data.success) {
        onFinished();
      } else {
        alert(response.data.error || "Hubo un error al guardar tu perfil. Revisa la consola.");
      }
    } catch (error) {
      alert("Error de conexión al servidor.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        bgcolor: '#f4f7fe',
        p: 3
      }}
    >
      <Paper 
        elevation={0} 
        sx={{ 
          maxWidth: 500, 
          width: '100%', 
          p: 4, 
          borderRadius: '24px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
          textAlign: 'center'
        }}
      >
        {step === 1 && (
          <Box>
            <Typography variant="h5" fontWeight={800} gutterBottom>
              Bienvenido a la Bolsa de Profesionales
            </Typography>
            <Typography color="textSecondary" mb={4}>
              Para empezar, dinos cómo planeas usar esta plataforma:
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => handleRoleSelection('enterprise')}
                sx={{
                  py: 4,
                  flexDirection: 'column',
                  borderRadius: '16px',
                  borderColor: '#eee',
                  color: '#211E26',
                  textTransform: 'none', // Para que no salga todo en mayúsculas
                  '&:hover': { borderColor: '#6C4DE2', bgcolor: 'rgba(108, 77, 226, 0.05)' }
                }}
              >
                <BusinessIcon sx={{ fontSize: 40, mb: 1, color: '#6C4DE2' }} />
                Soy una empresa o emprendedor
              </Button>

              <Button
                variant="outlined"
                onClick={() => handleRoleSelection('professional')}
                sx={{
                  py: 4,
                  flexDirection: 'column',
                  borderRadius: '16px',
                  borderColor: '#eee',
                  color: '#211E26',
                  textTransform: 'none', // Para que no salga todo en mayúsculas
                  '&:hover': { borderColor: '#F21C63', bgcolor: 'rgba(242, 28, 99, 0.05)' }
                }}
              >
                <WorkIcon sx={{ fontSize: 40, mb: 1, color: '#F21C63' }} />
                Soy un profesional o talento
              </Button>
            </Box>
          </Box>
        )}

        {step === 2 && (
          <Box>
            <Box display="flex" alignItems="center" mb={2}>
              <IconButton onClick={() => setStep(1)} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" fontWeight={800} ml={1}>
                Casi listo...
              </Typography>
            </Box>

            <Typography color="textSecondary" mb={3} textAlign="left">
              {formData.user_type === 'enterprise' 
                ? 'Ayúdanos a entender qué tipo de talento buscas.' 
                : 'Cuéntanos un poco sobre tu perfil profesional.'}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {formData.user_type === 'enterprise' && (
                <TextField
                  fullWidth
                  label="Nombre de tu Empresa o Proyecto"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                />
              )}

              <TextField
                select
                fullWidth
                label="Sector / Industria"
                name="main_industry"
                value={formData.main_industry}
                onChange={handleInputChange}
                SelectProps={{
                  onOpen: () => {
                  },
                  MenuProps: {
                    style: { zIndex: 99999 },
                    disablePortal: false,
                    PaperProps: {
                      style: { maxHeight: 300, zIndex: 99999 },
                      sx: {
                        '&::-webkit-scrollbar': { width: '8px', display: 'block' },
                        '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb': { background: '#bbb', borderRadius: '4px' },
                        '&::-webkit-scrollbar-thumb:hover': { background: '#999' }
                      }
                    }
                  }
                }}
              >
                {sectors.map((sec) => {
                  return (
                    <MenuItem key={sec.id} value={sec.nombre}>
                      {sec.nombre}
                    </MenuItem>
                  );
                })}
              </TextField>

              {formData.user_type === 'professional' ? (
                <>
                  <TextField
                    fullWidth
                    label="Años de experiencia"
                    name="experience_years"
                    type="number"
                    value={formData.experience_years}
                    onChange={handleInputChange}
                  />
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Biografía / Sobre mí"
                    name="bio"
                    placeholder="Cuéntale a las empresas un poco sobre tu trayectoria..."
                    value={formData.bio}
                    onChange={handleInputChange}
                  />
                  <TextField
                    select
                    fullWidth
                    label="Especialidad"
                    name="specialization"
                    value={formData.specialization}
                    onChange={handleInputChange}
                    SelectProps={{
                      MenuProps: { 
                        style: { zIndex: 99999 },
                        disablePortal: false,
                        PaperProps: { 
                          style: { maxHeight: 300, zIndex: 99999 },
                          sx: {
                            '&::-webkit-scrollbar': { width: '8px', display: 'block' },
                            '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
                            '&::-webkit-scrollbar-thumb': { background: '#bbb', borderRadius: '4px' },
                            '&::-webkit-scrollbar-thumb:hover': { background: '#999' }
                          }
                        } 
                      }
                    }}
                  >
                    {professionalTypes.map((prof) => (
                      <MenuItem key={prof.id} value={prof.id}>
                        <ListItemText primary={prof.nombre} secondary={prof.categoria} />
                      </MenuItem>
                    ))}
                  </TextField>
                </>
              ) : (
                <TextField
                  select
                  fullWidth
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((value) => {
                          const prof = professionalTypes.find((p) => p.id === value);
                          return <Chip key={value} label={prof ? prof.nombre : value} size="small" color="primary" variant="outlined" />;
                        })}
                      </Box>
                    ),
                    MenuProps: { 
                      style: { zIndex: 99999 },
                      disablePortal: false,
                      PaperProps: { 
                        style: { maxHeight: 300, zIndex: 99999 },
                        sx: {
                          '&::-webkit-scrollbar': { width: '8px', display: 'block' },
                          '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '4px' },
                          '&::-webkit-scrollbar-thumb': { background: '#bbb', borderRadius: '4px' },
                          '&::-webkit-scrollbar-thumb:hover': { background: '#999' }
                        }
                      } 
                    }
                  }}
                  label="¿Qué perfiles buscas? (Puedes elegir varios)"
                  name="specialization"
                  value={Array.isArray(formData.specialization) ? formData.specialization : []}
                  onChange={handleInputChange}
                >
                  {professionalTypes.map((prof) => (
                    <MenuItem key={prof.id} value={prof.id}>
                      <Checkbox checked={formData.specialization.indexOf(prof.id) > -1} />
                      <ListItemText primary={prof.nombre} secondary={prof.categoria} />
                    </MenuItem>
                  ))}
                </TextField>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  mt: 2,
                  borderRadius: '12px',
                  bgcolor: '#6C4DE2',
                  py: 1.5,
                  fontWeight: 'bold',
                  '&:hover': { bgcolor: '#5237b5' }
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Finalizar Registro'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BolsaOnboarding;
